import { useEffect, useMemo, useRef, useState } from 'react';
import {
  generateMultiTonePalette,
  isStarkHex,
  PALETTE_COPY,
  VIBE_FLAVOR,
  type MultiTonePalette,
  type PaletteRole,
} from '@flashpoint/site-dna';
import type { StepProps } from '../stepProps';

interface RowProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
}

function ColorRow({ label, value, onChange }: RowProps) {
  const stark = isStarkHex(value);
  return (
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 cursor-pointer rounded-md border border-nm-border bg-transparent"
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-32 rounded-md border border-nm-border bg-nm-bg px-2 py-1 font-mono text-xs text-nm-ink outline-none focus:border-nm-accent"
      />
      <span className="flex-1 text-xs text-nm-ink">{label}</span>
      {stark && (
        <span className="rounded bg-nm-danger/15 px-2 py-0.5 text-[10px] font-bold text-nm-danger">
          STARK
        </span>
      )}
    </div>
  );
}

export function PaletteStep({ lead, queueField }: StepProps) {
  const serverP = lead.siteDna.designTokens.multiTonePalette;
  const vibe = lead.siteDna.designTokens.vibe;
  const [p, setP] = useState(() => serverP);
  const dirty = useRef(false);

  useEffect(() => {
    setP(serverP);
    dirty.current = false;
  }, [lead.id]);

  useEffect(() => {
    if (dirty.current) return;
    setP(serverP);
  }, [serverP]);

  useEffect(() => {
    if (dirty.current && JSON.stringify(serverP) === JSON.stringify(p)) {
      dirty.current = false;
    }
  }, [serverP, p]);

  const update = (patch: Partial<MultiTonePalette>) => {
    dirty.current = true;
    const next = { ...p, ...patch };
    setP(next);
    queueField('siteDna.designTokens.multiTonePalette', next);
  };

  const updateArray = (key: 'warmTones' | 'coolTones' | 'surfaceVariants', i: number, hex: string) => {
    dirty.current = true;
    const arr = [...(p[key] ?? [])];
    arr[i] = hex;
    const next = { ...p, [key]: arr } as MultiTonePalette;
    setP(next);
    queueField('siteDna.designTokens.multiTonePalette', next);
  };

  const addToArray = (key: 'warmTones' | 'coolTones' | 'surfaceVariants') => {
    dirty.current = true;
    const arr = [...(p[key] ?? []), '#888888'];
    const next = { ...p, [key]: arr } as MultiTonePalette;
    setP(next);
    queueField('siteDna.designTokens.multiTonePalette', next);
  };

  const removeFromArray = (key: 'warmTones' | 'coolTones' | 'surfaceVariants', i: number) => {
    dirty.current = true;
    const arr = (p[key] ?? []).filter((_, idx) => idx !== i);
    const next = { ...p, [key]: arr } as MultiTonePalette;
    setP(next);
    queueField('siteDna.designTokens.multiTonePalette', next);
  };

  const validation = useMemo(() => {
    const issues: string[] = [];
    const flat = [
      p.accent,
      ...(p.ink ? [p.ink] : []),
      ...(p.muted ? [p.muted] : []),
      ...p.warmTones,
      ...p.coolTones,
      ...p.surfaceVariants,
    ];
    if (flat.some(isStarkHex)) {
      issues.push('Pure white (#FFFFFF) and pure black (#000000) are forbidden. Soften them.');
    }
    if (p.warmTones.length === 0 && p.coolTones.length === 0) {
      issues.push('Add at least one warm or cool tone.');
    }
    return issues;
  }, [p]);

  const flavor = VIBE_FLAVOR[vibe];

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-nm-border bg-nm-moss/30 p-3 text-xs">
        <p className="font-mono uppercase tracking-widest text-nm-muted">Under the Nuzum Method</p>
        <p className="mt-1.5 text-nm-ink">
          The obsidian / moss / paper base, the leak colors, and the type roles are owned by the
          signature - they don't change here. <span className="font-semibold text-nm-ink">Accent</span> is the
          one token most clients ever override. The other tokens below are kept around for the
          legacy flat-vibe rendering and almost never need editing under the Nuzum Method.
        </p>
        <p className="mt-2 italic text-nm-muted">
          Accent behavior for {vibe}: {flavor.accentBehavior}
        </p>
      </div>
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-nm-muted">
          Multi-tone palette derived from <strong>{vibe}</strong>. The Stark colors (#FFFFFF and
          #000000) are blocked - use warm off-whites or deep slates instead.
        </p>
        <button
          type="button"
          onClick={() => {
            const next = generateMultiTonePalette(vibe);
            dirty.current = true;
            setP(next);
            queueField('siteDna.designTokens.multiTonePalette', next);
          }}
          className="rounded-md border border-nm-border px-2.5 py-1 text-xs text-nm-ink hover:border-nm-accent"
        >
          Reset to vibe defaults
        </button>
      </div>

      <section className="space-y-2">
        <RoleHeader role="accent" />
        <ColorRow label="accent" value={p.accent} onChange={(v) => update({ accent: v })} />
        <RoleHeader role="ink" />
        <ColorRow
          label="ink (text)"
          value={p.ink ?? '#0f172a'}
          onChange={(v) => update({ ink: v })}
        />
        <RoleHeader role="muted" />
        <ColorRow
          label="muted (secondary text)"
          value={p.muted ?? '#94a3b8'}
          onChange={(v) => update({ muted: v })}
        />
      </section>

      {(['warmTones', 'coolTones', 'surfaceVariants'] as const).map((key) => (
        <section key={key} className="space-y-2">
          <div className="flex items-center justify-between">
            <RoleHeader role={key} />
            <button
              type="button"
              onClick={() => addToArray(key)}
              className="rounded-md border border-nm-border px-2 py-1 text-xs text-nm-ink hover:border-nm-accent"
            >
              + Add
            </button>
          </div>
          {(p[key] ?? []).length === 0 && (
            <p className="text-xs text-nm-muted">None.</p>
          )}
          {(p[key] ?? []).map((hex, i) => (
            <div key={i} className="flex items-center gap-2">
              <ColorRow label={`${key}[${i}]`} value={hex} onChange={(v) => updateArray(key, i, v)} />
              <button
                type="button"
                onClick={() => removeFromArray(key, i)}
                className="text-xs text-nm-danger hover:opacity-80"
              >
                Remove
              </button>
            </div>
          ))}
        </section>
      ))}

      {validation.length > 0 && (
        <div className="rounded-md border border-nm-danger/30 bg-nm-danger-bg p-3 text-xs text-nm-danger">
          <div className="font-semibold">Palette issues:</div>
          <ul className="mt-1 list-disc pl-4">
            {validation.map((v) => (
              <li key={v}>{v}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-[11px] uppercase tracking-widest text-nm-muted">{children}</div>;
}

function RoleHeader({ role }: { role: PaletteRole }) {
  const meta = PALETTE_COPY[role];
  return (
    <div>
      <SectionLabel>{meta.name}</SectionLabel>
      <p className="mt-0.5 text-xs text-nm-muted">{meta.blurb}</p>
      <p className="mt-0.5 text-[11px] italic text-nm-muted">
        Override · {meta.whenToOverride}
      </p>
    </div>
  );
}
